<template>
  <div v-if="loading">
    <Card :loadingState="loading" />
    <Card :loadingState="loading" />
  </div>
  <ErrorTile v-else-if="showErrorTile" :title="$t('policyList.errorTitle')"></ErrorTile>
  <template v-else>
    <DynamicPolicyTile
      v-for="(policy, index) of policies"
      :key="index"
      :isDetailHeader="false"
      :policy="policy"
    />
    <Card
      v-if="showPortalUnderConstructionTile"
      :title="$t('policyList.noContractsFoundTitle')"
    >
      <template v-slot:content>
        <div
          v-dompurify-html="$t('policyList.noContractsFoundText')"
          class="line-breaks"
        ></div>
      </template>
    </Card>
  </template>
</template>

<script lang="ts" setup>
import DynamicPolicyTile from '@/components/policy/DynamicPolicyTile.vue'
import ErrorTile from '@/components/common/ErrorTile.vue'
import type { Policy } from 'policies-api'
import { Card } from 'de-zurich-ui-library'
import { computed, onMounted, type Ref, ref } from 'vue'
import { isTrue } from '@/utils/CommonUtils'
import { usePoliciesStore } from '@/stores/policiesStore'
import type { ShowOrDeleteQuotes } from '@/types/policy/PolicyTypes'
import {
  fetchAllQuoteIdsByPartnerProxyId,
  fetchQuoteShowOrDelete
} from '@/stores/policiesService'

const props = defineProps({
  partnerProxyId: {
    type: String,
    required: true
  }
})

const loading = ref(true)
const showPortalUnderConstructionTile = ref(false)
const lockedContractsCount = ref(-1)
const policies: Ref<Policy[]> = ref([])
const policyBlacklist: Ref<Set<string>> = ref(new Set<string>())
const showErrorTile = ref(false)

// if count > 0 => true => hide tile
// if count == 0 => false => show tile
const userNeedsIdLvlUpgrade = computed(() => {
  return lockedContractsCount.value > 0
})

function isEveryContractHidden() {
  return policies.value.length === 0 || allPoliciesAreBlacklisted.value
}

const showConstructionTile = computed(() => {
  // show construction tile when
  // flag is true
  // there are no policies or all policies are blacklisted
  // and there are no locked contracts (due to low id level)
  return (
    isTrue(globalThis.ENV_CONFIG?.FEATURE_FLAGS.POLICIES.SHOW_CONSTRUCTION_TILE) &&
    isEveryContractHidden() &&
    !userNeedsIdLvlUpgrade.value
  )
})

onMounted(async () => {
  try {
    policies.value = await usePoliciesStore().getAllPoliciesByPartnerProxyId(
      props.partnerProxyId
    )
    lockedContractsCount.value = await usePoliciesStore().getLockedContractsCount(
      props.partnerProxyId
    )
  } catch (e) {
    console.error('PolicyList Error', e)
    showErrorTile.value = true
  }
  loading.value = false
  showPortalUnderConstructionTile.value = await calcPortalUnderConstructionTile()
})

async function calcPortalUnderConstructionTile() {
  return (
    showConstructionTile.value &&
    // list is not loading
    !loading.value &&
    // no contracts are shown
    isEveryContractHidden() &&
    // user does NOT need idLevel upgrade
    !userNeedsIdLvlUpgrade.value &&
    // show tile only if there are no quotes shown
    !(await isQuotesShown())
  )
}

async function isQuotesShown(): Promise<boolean> {
  const quotes: Map<string, ShowOrDeleteQuotes> = new Map<string, ShowOrDeleteQuotes>()
  let temp_quotes: string[] = []
  // get all quoteIds
  try {
    temp_quotes = await fetchAllQuoteIdsByPartnerProxyId(props.partnerProxyId)
  } catch {
    return false
  }

  if (temp_quotes.length === 0) {
    return false
  }

  // check each quoteId if it is valid to be shown
  try {
    for (const id of temp_quotes) {
      const single_quote_response: ShowOrDeleteQuotes = await fetchQuoteShowOrDelete(id)
      if (single_quote_response.showCard) quotes.set(id, single_quote_response)
    }
  } catch {
    return quotes.size != 0
  }
  return quotes.size != 0
}

const allPoliciesAreBlacklisted = computed(() => {
  const visiblePolicies =
    policies.value.filter((p) => {
      return !policyBlacklist.value.has(p.policyId)
    }) || []

  return visiblePolicies.length < 1
})
</script>

<style lang="scss" scoped>
.line-breaks {
  white-space: pre-wrap;
}
</style>
